/**
 * sliceSort.js — правильный порядок срезов в серии.
 *
 * Проблема: сортировка по Slice Location (0020,1041) ломается на МРТ —
 * тег там часто отсутствует. Надёжный способ (как в RadiAnt/OHIF):
 * проекция Image Position Patient (0020,0032) на нормаль плоскости,
 * вычисленную из Image Orientation Patient (0020,0037).
 *
 * Fallback-цепочка:
 *   1. IPP·normal — если у всех срезов есть ipp/iop и проекции различны;
 *   2. Slice Location -> Instance Number — старое поведение
 *      (покрывает multiframe-кадры и мульти-эхо, где позиции совпадают).
 */

/**
 * Сортирует массив срезов на месте и возвращает его.
 * Каждый элемент может содержать: ipp:[x,y,z], iop:[6], sliceLocation, instanceNum.
 * @param {Array<Object>} images
 * @returns {Array<Object>}
 */
export function sortSlices(images) {
  if (!Array.isArray(images) || images.length < 2) return images;

  const hasGeometry = images.every(im =>
    Array.isArray(im.ipp) && im.ipp.length === 3 && im.ipp.every(Number.isFinite) &&
    Array.isArray(im.iop) && im.iop.length === 6 && im.iop.every(Number.isFinite)
  );

  if (hasGeometry) {
    // Нормаль = row × col (по первому срезу серии)
    const [r1, r2, r3, c1, c2, c3] = images[0].iop;
    const n = [r2 * c3 - r3 * c2, r3 * c1 - r1 * c3, r1 * c2 - r2 * c1];

    for (const im of images) {
      im._sortKey = im.ipp[0] * n[0] + im.ipp[1] * n[1] + im.ipp[2] * n[2];
    }

    // [7.0.57] Раньше здесь стояло жёсткое условие: если ХОТЬ ДВЕ проекции
    // совпали — вся серия уходила в fallback по sliceLocation/instanceNum.
    // Замысел верный (мульти-эхо и повторы действительно требуют instanceNum),
    // но цена непомерная: ОДИН продублированный файл в папке или повтор
    // инстанса в выгрузке PACS выключал геометрическую сортировку целиком.
    // Замерено на наклонённой на 12° МР-серии с чересстрочной нумерацией:
    //   без дубля      -> 0,1,2,3,4,5,6,7,8,9,10,11
    //   с одним дублем -> 0,2,4,6,8,10,1,3,5,7,9,11,6   (порядок съёмки!)
    // Отказ молчаливый, а последствие — перепутанная стопка: MPR, объём
    // опухоли и наведение считаются по неверной геометрии.
    //
    // Теперь: проекция остаётся ГЛАВНЫМ ключом всегда, а совпадения
    // разрешаются номером инстанса — это и мульти-эхо обслуживает (кадры с
    // одной позицией идут подряд в порядке съёмки), и одиночный дубль больше
    // ничего не ломает.
    const distinct = new Set(images.map(im => im._sortKey.toFixed(3)));
    const repeats = images.length - distinct.size;
    images.sort((a, b) => (a._sortKey - b._sortKey)
      || ((a.instanceNum || 0) - (b.instanceNum || 0)));
    if (repeats > 0) {
      try {
        // Молчать нельзя: повторы позиций — либо дубли файлов, либо мульти-эхо.
        // И то и другое врач должен видеть.
        (globalThis.console?.warn || (() => {}))(
          `[sliceSort] Совпадающих позиций срезов: ${repeats} из ${images.length}. `
          + 'Похоже на дубли файлов или мульти-эхо. Порядок задан геометрией, '
          + 'совпадения разрешены номером инстанса.');
      } catch (e) { /* */ }
    }
    images._positionRepeats = repeats;
    return images;
  }

  // Fallback: старое поведение
  images.sort((a, b) => {
    const sa = a.sliceLocation ?? 0;
    const sb = b.sliceLocation ?? 0;
    return sa !== sb ? sa - sb : (a.instanceNum || 0) - (b.instanceNum || 0);
  });
  return images;
}
